import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { QueryClient, QueryClientProvider } from "react-query";
import { baseTheme } from "./theme/theme";
import { authTheme } from "./theme/authTheme";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import LoginPage from "./pages/auth/LoginPage";
import HomePage from "./pages/HomePage";
import VerifyEmailPage from "./pages/auth/VerifyEmailPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import InvitePage from "./pages/auth/InvitePage";
import { UsersPage } from "./pages/UsersPage";
import { Layout } from "./components/Layout";
import { ToastContainer } from "./components/toast/ToastContainer";
import { TopProgressBar } from "./components/TopProgressBar";
import { CostEstimationShell } from "./pages/cost-estimation/CostEstimationShell";
import { PreventivoListPage } from "./pages/cost-estimation/PreventivoListPage";
import { PreventivoWorkspacePage } from "./pages/cost-estimation/PreventivoWorkspacePage";
import { ManualSearchPage } from "./pages/cost-estimation/ManualSearchPage";
import { CatalogImportPage } from "./pages/cost-estimation/CatalogImportPage";
import "./App.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      // Keep unused query data in memory for 5 min so navigating back is instant.
      cacheTime: 5 * 60 * 1000,
    },
  },
});

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const AuthThemeRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <ThemeProvider theme={authTheme}>
    <CssBaseline />
    {children}
  </ThemeProvider>
);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={baseTheme}>
        <CssBaseline />
        <AuthProvider>
          <Router>
            <Routes>
              <Route
                path="/login"
                element={
                  <AuthThemeRoute>
                    <LoginPage />
                  </AuthThemeRoute>
                }
              />
              <Route path="/register" element={<Navigate to="/login" replace />} />
              <Route
                path="/verify-email"
                element={
                  <AuthThemeRoute>
                    <VerifyEmailPage />
                  </AuthThemeRoute>
                }
              />
              <Route
                path="/forgot-password"
                element={
                  <AuthThemeRoute>
                    <ForgotPasswordPage />
                  </AuthThemeRoute>
                }
              />
              <Route
                path="/reset-password"
                element={
                  <AuthThemeRoute>
                    <ResetPasswordPage />
                  </AuthThemeRoute>
                }
              />
              <Route
                path="/invite"
                element={
                  <AuthThemeRoute>
                    <InvitePage />
                  </AuthThemeRoute>
                }
              />

              {/* App home */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <HomePage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Users (admin) */}
              <Route
                path="/users"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <UsersPage />
                    </Layout>
                  </ProtectedRoute>
                }
              />

              {/* Cost Estimation — full-route tool with internal shell */}
              <Route
                path="/cost-estimation"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <CostEstimationShell />
                    </Layout>
                  </ProtectedRoute>
                }
              >
                <Route index element={<PreventivoListPage />} />
                <Route path="search" element={<ManualSearchPage />} />
                <Route path="catalog-import" element={<CatalogImportPage />} />
                <Route path=":id" element={<PreventivoWorkspacePage />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <TopProgressBar />
            <ToastContainer />
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
